import "./TextInput.scss";

import cx from "classnames";
import React, { useCallback, useRef } from "react";
import type { FieldErrors, RegisterOptions } from "react-hook-form";
import { getTimeFromISOString } from "utils/date-utils";

export type TextInputProps = {
  label?: string;
  name: string;
  errors?: FieldErrors;
  readOnly?: boolean | (() => boolean);
  classNames?: string;
  rules?: RegisterOptions;
  forceToNull?: boolean;
  roundDate?: boolean;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  isFocused?: boolean;
  maxLength?: number;
} & React.InputHTMLAttributes<HTMLInputElement>;

const TextInput = React.forwardRef<HTMLInputElement, TextInputProps>(
  (
    {
      readOnly,
      label,
      name,
      errors,
      value = "",
      forceToNull,
      roundDate,
      onBlur,
      classNames,
      isFocused,
      maxLength,
      ...rest
    },
    ref,
  ) => {
    const initialValueRef = useRef(value);

    const handleBlur = useCallback(
      (event: React.FocusEvent<HTMLInputElement>) => {
        if (event.target.value !== initialValueRef.current && onBlur) {
          onBlur(event);
        }
      },
      [onBlur],
    );

    const handleFocus = useCallback(() => {
      initialValueRef.current = value;
    }, [value]);

    const displayValue =
      roundDate && typeof value === "string"
        ? getTimeFromISOString(value, "date")
        : value;

    const resolvedReadOnly =
      // @ts-expect-error - readOnly is a function or a boolean
      typeof readOnly === "function" ? readOnly(value) : readOnly;
    return (
      <div
        className={cx("input", classNames, {
          "input--error": errors && errors[name],
          "input--disabled": resolvedReadOnly,
          "input--focused": isFocused,
        })}
        role={errors && errors[name] && "alert"}
        title={errors && (errors[name]?.message as string)}
      >
        {label && <label htmlFor={name}>{label}</label>}
        <input
          autoComplete="off"
          disabled={resolvedReadOnly}
          ref={ref}
          value={forceToNull ? displayValue || null : displayValue}
          name={name}
          type="text"
          onBlur={handleBlur}
          onFocus={handleFocus}
          maxLength={maxLength}
          {...rest}
        />
      </div>
    );
  },
);

TextInput.displayName = "Input";

export default TextInput;
